import { useSgReadyStatusQuery } from 'src/Libs/ai-360/hooks'
import useCheckDynamicPowerPriceApplicability from 'src/Libs/dynamic-power-prices/hooks/use-check-dynamic-power-price-applicability'
import { EmsSgReadyMode } from 'src/Libs/dynamic-power-prices/types'
import { useFarmContext } from 'src/Elements/Farm'
import { useInstallationDevices, useInstallationSubDevices } from 'src/Libs/flow/installation/hooks'
import { isFlowTheme } from 'src/Libs/utils'

interface Props {
  installationId?: string
  storageId?: string
}

interface ReturnType {
  isLoading: boolean
  sgReadyStatus: EmsSgReadyMode | null
  isDynamicPowerPriceEnabledForFarm: boolean
  threeSixtyAutarkyHintKey: string
  hideSgReadyBadge: boolean
}

/**
 * Hook to determine the SG ready badge visibility for the live view,
 * the dynamic power price applicability for the farm and the hint key for the 360 autarky note
 * @param installationId the installation id of the current installation
 * @param storageId the storage id of the current installation
 * @returns the SG ready badge visibility status, the SG ready status, the dpp-applicability for the farm and the hint key for the 360 note
 */
const useSgReadyBadgeForLiveView = ({ installationId, storageId }: Props): ReturnType => {
  const installationIdNumber = installationId ? Number.parseInt(installationId) : undefined
  const { data: devices } = useInstallationDevices(installationIdNumber, isFlowTheme)
  const emcId = devices?.emc?.id?.toString() ?? null
  const { data: subDeviceLinks, isLoading: areSubDeviceLinksLoading } = useInstallationSubDevices(installationIdNumber, emcId, isFlowTheme)

  const { isFarm, farmMaster, isFarmMasterSelected } = useFarmContext()
  const storageIdToUseForDpp = isFarm && farmMaster ? farmMaster.storageId : storageId
  const { isDynamicPowerPriceEnabled } = useCheckDynamicPowerPriceApplicability(storageIdToUseForDpp ?? '')
  const isDynamicPowerPriceEnabledForFarm = isDynamicPowerPriceEnabled && !isFlowTheme
  const threeSixtyAutarkyHintKey =
    isFarm && !isFarmMasterSelected ? 'live-view.three-sixty-autarky.note-client' : 'live-view.three-sixty-autarky.note'

  const { isLoading, sgReadyStatus } = useSgReadyStatusQuery(storageIdToUseForDpp)

  const hvacInterfaces = subDeviceLinks?.controllers?.filter(
    ioInterface => !!ioInterface.outputs?.find(output => output.deviceType === 'HVAC') || ioInterface.type === 'HpWithISG'
  )
  const hasSgReadyHeatpump = !!hvacInterfaces?.some(io => io.type === 'HpWithISG')
  const hasSgReadyStorage = !!hvacInterfaces?.some(io => io.media === 'Storage')
  const hideHTCDevices = hasSgReadyHeatpump || hasSgReadyStorage
  const ioDevicesToDisplay = hideHTCDevices ? hvacInterfaces?.filter(io => io.media !== 'ModbusRTU') : hvacInterfaces

  const hideBadgeForFlow = isFlowTheme && (!ioDevicesToDisplay || ioDevicesToDisplay.length === 0 || areSubDeviceLinksLoading)
  // For e3dc-theme subdevice-links are empty → hide badge if sgReadyStatus is disabled
  const hideSgReadyBadge = hideBadgeForFlow || (sgReadyStatus?.disabled ?? false)

  return {
    isLoading,
    sgReadyStatus: sgReadyStatus?.state ?? null,
    isDynamicPowerPriceEnabledForFarm,
    threeSixtyAutarkyHintKey,
    hideSgReadyBadge,
  }
}

export default useSgReadyBadgeForLiveView
